import type { CopyKey } from "./i18n/copyTypes";
import type { FlowStep, ManualPage, RequestPath, View } from "./types";

interface QuoteStart {
  view: View;
  path: RequestPath;
}

export interface QuoteNav {
  view: View;
  page?: ManualPage;
}

export class QuotePathResolver {
  readonly steps = ["Route & Order", "Shipment", "Additional services", "Contact", "Review"] as const;

  pathsFor(flow: readonly FlowStep[]): RequestPath[] {
    const allowForm = flow.includes("form") || flow.length === 0;
    const allowUpload = flow.includes("upload") || flow.length === 0;
    const paths: RequestPath[] = [];
    if (allowForm) paths.push("manual");
    if (allowUpload) paths.push("quickfill");
    return paths;
  }

  start(flow: readonly FlowStep[]): QuoteStart {
    const paths = this.pathsFor(flow);
    if (paths.length === 1 && paths[0] === "quickfill") {
      return { view: "quickfill", path: "quickfill" };
    }
    if (flow[0] === "upload") return { view: "quickfill", path: "quickfill" };
    return { view: "choose", path: "manual" };
  }

  stepIndex(page: ManualPage, view: View): number {
    if (view === "result") return 5;
    if (view === "review") return 4;
    if (page === "route") return 0;
    if (page === "shipment") return 1;
    if (page === "services") return 2;
    return 3;
  }

  continueFrom(page: ManualPage): QuoteNav {
    if (page === "route") return { view: "form", page: "shipment" };
    if (page === "shipment") return { view: "form", page: "services" };
    if (page === "services") return { view: "form", page: "contact" };
    return { view: "review" };
  }

  backFrom(page: ManualPage, path: RequestPath): QuoteNav {
    if (page === "contact") return { view: "form", page: "services" };
    if (page === "services") return { view: "form", page: "shipment" };
    if (page === "shipment") return { view: "form", page: "route" };
    return { view: path === "quickfill" ? "quickfill" : "choose" };
  }

  leaveQuickFill(flow: readonly FlowStep[], draftApplied: boolean): QuoteNav | null {
    const hasManual = this.pathsFor(flow).includes("manual");
    if (flow[0] === "upload" && hasManual) return { view: "form", page: "route" };
    if (hasManual) return { view: "choose" };
    if (draftApplied) return { view: "form", page: "route" };
    return null;
  }

  backLabelKey(flow: readonly FlowStep[], view: View, page?: ManualPage): CopyKey {
    if (flow[0] !== "upload") return "nav.back";
    if (view === "quickfill") return "choose.manual.title";
    if (view === "form" && page === "route") return "choose.quickfill.title";
    return "nav.back";
  }

  viewForPath(path: RequestPath): View {
    return path === "manual" ? "form" : "quickfill";
  }
}
